<template>
  <div class="ei-general-scale">
    <div
        v-if="showTitle"
        class="ei-general-scale__title-row"
    >
      <span class="ei-general-scale__title-line"></span>

      <h2 class="ei-general-scale__title">
        {{ title }}
      </h2>
    </div>

    <a-card
        class="ei-general-scale__card"
        :bordered="true"
    >
      <div class="ei-general-scale__header">
        <h3 class="ei-general-scale__card-title">
          {{ cardTitle }}
        </h3>

        <p
            v-if="description"
            class="ei-general-scale__description"
        >
          {{ description }}
        </p>
      </div>

      <div
          v-if="showLegend && !useGradient"
          class="ei-general-scale__legend"
      >
        <div
            v-for="item in legendItems"
            :key="item.key"
            class="ei-general-scale__legend-item"
        >
          <span
              class="ei-general-scale__legend-mark"
              :style="{ backgroundColor: item.color }"
          ></span>

          <span>{{ item.label }}</span>
        </div>
      </div>

      <div
          v-if="useGradient"
          class="ei-general-scale__bars ei-general-scale__bars--gradient"
      >
        <div
            class="ei-general-scale__bar ei-general-scale__bar--gradient"
            :style="{ '--gradient-fill-width': getWidth(value) + '%' }"
        >
          <div
              class="ei-general-scale__bar-gradient"
              :style="{ background: gradient }"
          ></div>

          <div class="ei-general-scale__bar-mask"></div>

          <span
              class="ei-general-scale__bar-value"
              :style="{ left: getWidth(value) + '%' }"
          >
      {{ formatValue(value) }}
    </span>
        </div>
      </div>

      <div
          v-else
          class="ei-general-scale__bars"
      >
        <div class="ei-general-scale__bar">
          <div
              class="ei-general-scale__bar-fill"
              :style="{
              width: getWidth(compareValue) + '%',
              backgroundColor: compareColor,
            }"
          ></div>

          <span
              class="ei-general-scale__bar-value"
              :style="{ left: getWidth(compareValue) + '%' }"
          >
            {{ formatValue(compareValue) }}
          </span>
        </div>

        <div class="ei-general-scale__bar">
          <div
              class="ei-general-scale__bar-fill"
              :style="{
              width: getWidth(value) + '%',
              backgroundColor: mainColor,
            }"
          ></div>

          <span
              class="ei-general-scale__bar-value"
              :style="{ left: getWidth(value) + '%' }"
          >
            {{ formatValue(value) }}
          </span>
        </div>
      </div>

      <div class="ei-general-scale__scale">
        <div
            v-for="item in scaleLabels"
            :key="item.key"
            class="ei-general-scale__scale-item"
            :class="{
            'ei-general-scale__scale-item--number': item.type === 'number',
            'ei-general-scale__scale-item--text': item.type === 'text',
          }"
        >
          <span
              v-if="item.type === 'number'"
              class="ei-general-scale__scale-divider"
          ></span>

          <span
              v-if="item.type === 'number'"
              class="ei-general-scale__scale-number"
          >
            {{ item.value }}
          </span>

          <span
              v-if="item.type === 'text'"
              class="ei-general-scale__scale-label"
          >
            {{ item.label }}
          </span>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'EiGeneralScale',

  props: {
    showTitle: {
      type: Boolean,
      default: true,
    },

    title: {
      type: String,
      default: 'Эмоциональный интеллект',
    },

    cardTitle: {
      type: String,
      default: 'Средний общий ЭИ',
    },

    description: {
      type: String,
      default: 'Уровень совокупности когнитивных способностей: саморегуляция, влияние на эмоции других, эмпатия, анализ изменений эмоциональных реакций, сознательное применение энергии эмоций для усиления деятельности и мышления.',
    },

    value: {
      type: Number,
      default: 0,
    },

    compareValue: {
      type: Number,
      default: 0,
    },

    mainLabel: {
      type: String,
      default: 'Руководители проектов',
    },

    compareLabel: {
      type: String,
      default: 'Отраслевой ЭИ-профиль',
    },

    mainColor: {
      type: String,
      default: '#85A5FF',
    },

    compareColor: {
      type: String,
      default: '#D5DBE2',
    },

    min: {
      type: Number,
      default: 50,
    },

    max: {
      type: Number,
      default: 150,
    },

    showLegend: {
      type: Boolean,
      default: true,
    },

    useGradient: {
      type: Boolean,
      default: false,
    },

    gradient: {
      type: String,
      default: 'linear-gradient(90deg, #F55565 0%, #FF9F43 30%, #DDD71D 50%, #99DA6E 70%, #21AC0C 100%)',
    },
  },

  setup(props) {
    const legendItems = computed(() => [
      {
        key: 'compare',
        label: props.compareLabel,
        color: props.compareColor,
      },
      {
        key: 'main',
        label: props.mainLabel,
        color: props.mainColor,
      },
    ])

    const scaleLabels = computed(() => [
      {
        key: 'min',
        type: 'number',
        value: props.min,
      },
      {
        key: 'low',
        type: 'text',
        label: 'Низкий',
      },
      {
        key: 'eighty',
        type: 'number',
        value: 80,
      },
      {
        key: 'average',
        type: 'text',
        label: 'Средний',
      },
      {
        key: 'hundred',
        type: 'number',
        value: 100,
      },
      {
        key: 'high',
        type: 'text',
        label: 'Высокий',
      },
      {
        key: 'one-twenty',
        type: 'number',
        value: 120,
      },
      {
        key: 'highest',
        type: 'text',
        label: 'Высший',
      },
      {
        key: 'max',
        type: 'number',
        value: props.max,
      },
    ])

    const toNumber = (value) => {
      const number = Number(value)

      return Number.isFinite(number) ? number : 0
    }

    const getWidth = (value) => {
      const number = toNumber(value)

      if (number <= props.min) {
        return 0
      }

      if (number >= props.max) {
        return 100
      }

      return Number((((number - props.min) / (props.max - props.min)) * 100).toFixed(2))
    }

    const formatValue = (value) => {
      const number = toNumber(value)

      return Number.isInteger(number)
          ? number
          : number.toFixed(1)
    }

    return {
      legendItems,
      scaleLabels,
      getWidth,
      formatValue,
    }
  },
}
</script>

<style scoped lang="scss">
.ei-general-scale {
  width: 100%;
  text-align: start;
}

.ei-general-scale__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 20px;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
}

.ei-general-scale__title-line {
  flex: 0 0 auto;
  width: 4px;
  height: 24px;
  border-radius: 8px;
  background-color: var(--main-green);
}

.ei-general-scale__title {
  margin: 0;
  font-size: 20px;
  line-height: 120%;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.ei-general-scale__card {
  border-radius: 8px;
}

.ei-general-scale__card :deep(.ant-card-body) {
  padding: 18px 16px 16px;
}

.ei-general-scale__header {
  margin: 0 0 18px;
}

.ei-general-scale__card-title {
  margin: 0 0 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-general-scale__description {
  max-width: 940px;
  margin: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.ei-general-scale__legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin: 0 0 16px;
}

.ei-general-scale__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.ei-general-scale__legend-mark {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 2px;
}

.ei-general-scale__bars {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
}

.ei-general-scale__bars--gradient {
  gap: 0;
}

.ei-general-scale__bar {
  position: relative;
  width: 100%;
  height: 36px;
  overflow: visible;
  border-radius: 7px;
  background-color: #eef1f5;
}

.ei-general-scale__bar-fill {
  height: 100%;
  border-radius: 7px 0 0 7px;
}

.ei-general-scale__bar-value {
  position: absolute;
  top: 50%;
  z-index: 2;
  transform: translate(10px, -50%);
  white-space: nowrap;
  font-weight: 400;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-general-scale__scale {
  display: grid;
  grid-template-columns: 34px 1fr 34px 1fr 34px 1fr 34px 1fr 34px;
  align-items: start;
  min-height: 34px;
  margin-top: 0;
  font-size: 12px;
  line-height: 110%;
}

.ei-general-scale__scale-item {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  white-space: nowrap;
}

.ei-general-scale__scale-item--number {
  position: relative;
  padding-top: 20px;
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.ei-general-scale__scale-divider {
  position: absolute;
  top: 0;
  left: 50%;
  width: 1px;
  height: 18px;
  transform: translateX(-50%);
  margin: 0;
  background: rgba(0, 0, 0, 0.15);
}

.ei-general-scale__scale-number {
  white-space: nowrap;
}

.ei-general-scale__scale-label {
  padding-top: 20px;
  font-weight: 400;
  font-size: 12px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-general-scale__bar--gradient {
  position: relative;
  overflow: hidden;
  background-color: #eef1f5;
}

.ei-general-scale__bar-gradient {
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: 7px;
}

.ei-general-scale__bar-mask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: var(--gradient-fill-width);
  z-index: 2;
  background-color: #eef1f5;
}

.ei-general-scale__bar--gradient .ei-general-scale__bar-value {
  z-index: 3;
}

@media (max-width: $mobile-breakpoint) {
  .ei-general-scale__title {
    font-size: 18px;
  }

  .ei-general-scale__card :deep(.ant-card-body) {
    padding: 14px 12px;
  }

  .ei-general-scale__bar {
    height: 28px;
  }

  .ei-general-scale__bar-value {
    font-size: 15px;
  }

  .ei-general-scale__scale {
    grid-template-columns: 28px 1fr 28px 1fr 28px 1fr 28px 1fr 28px;
  }

  .ei-general-scale__scale-label {
    max-width: 34px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>